/**
 * White-Label System — Multi-Client Branding Engine
 *
 * Resolves tenant/brand configuration from:
 * 1. Custom domain (client.yourapp.com → programCode)
 * 2. URL path (/p/CODE → programCode)
 * 3. Fallback (default branding)
 *
 * Caches to localStorage for instant no-flicker load.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface BrandConfig {
  programId: string | null;
  programCode: string | null;
  name: string;
  logo: string;
  favicon: string;
  sidebarLogo: string;
  theme: BrandThemeTokens;
  domain: string | null;
  loginBackground: LoginBackgroundConfig;
  loginPageTitle: string;
  loginPageSubtitle: string;
  loginPageDescription: string;
  resolvedAt: string;
}

export interface BrandThemeTokens {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
}

export interface LoginBackgroundConfig {
  type: 'gradient' | 'solid' | 'image';
  value: string;
  patternEnabled: boolean;
}

export interface DomainMapping {
  domain: string;
  programCode: string;
  programId: string;
  isVerified: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const BRAND_CACHE_KEY = 'epprem-brand-config';
const DOMAIN_MAP_CACHE_KEY = 'epprem-domain-map';
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

// Known app domains (not client custom domains)
const APP_DOMAINS = [
  'localhost',
  '127.0.0.1',
  'epprem.com',
  'app.epprem.com',
  'staging.epprem.com',
];

// ─────────────────────────────────────────────────────────────────────────────
// Default Brand
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_BRAND: BrandConfig = {
  programId: null,
  programCode: null,
  name: 'EPPREM',
  logo: '',
  favicon: '',
  sidebarLogo: '',
  theme: {
    primaryColor: 'primary',
    secondaryColor: 'textMuted',
    accentColor: 'warning',
  },
  domain: null,
  loginBackground: {
    type: 'gradient',
    value: '',
    patternEnabled: true,
  },
  loginPageTitle: 'EPPREM',
  loginPageSubtitle: 'Enhanced Patient Support Program',
  loginPageDescription: 'Empowering healthcare teams to deliver personalized patient care.',
  resolvedAt: '',
};

// ─────────────────────────────────────────────────────────────────────────────
// Domain Detection
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Detect if the current hostname is a custom client domain
 * (i.e., not one of the known app domains).
 */
export function detectCustomDomain(): string | null {
  const hostname = window.location.hostname.toLowerCase();

  // Check if it's a known app domain
  if (APP_DOMAINS.some(d => hostname === d || hostname.endsWith(`.${d}`))) {
    return null;
  }

  // It's a custom domain
  return hostname;
}

/**
 * Extract program code from subdomain pattern: {code}.app.epprem.com
 */
export function extractCodeFromSubdomain(): string | null {
  const hostname = window.location.hostname.toLowerCase();

  // Pattern: {code}.app.epprem.com or {code}.epprem.com
  const subdomainMatch = hostname.match(/^([a-z0-9_-]+)\.(app\.)?epprem\.com$/);
  if (subdomainMatch && subdomainMatch[1] !== 'www' && subdomainMatch[1] !== 'staging') {
    return subdomainMatch[1].toUpperCase();
  }

  return null;
}

/**
 * Full tenant resolution from hostname.
 * Returns program code if detectable, null otherwise.
 */
export function resolveTenantFromHostname(): { type: 'subdomain' | 'custom-domain' | 'none'; value: string | null } {
  // First check subdomain pattern
  const subdomainCode = extractCodeFromSubdomain();
  if (subdomainCode) {
    return { type: 'subdomain', value: subdomainCode };
  }

  // Check custom domain
  const customDomain = detectCustomDomain();
  if (customDomain) {
    return { type: 'custom-domain', value: customDomain };
  }

  return { type: 'none', value: null };
}

// ─────────────────────────────────────────────────────────────────────────────
// Cache Operations
// ─────────────────────────────────────────────────────────────────────────────

interface CachedBrand {
  brand: BrandConfig;
  cachedAt: number;
}

export function getCachedBrand(key?: string): BrandConfig | null {
  try {
    const cacheKey = key ? `${BRAND_CACHE_KEY}:${key}` : BRAND_CACHE_KEY;
    const raw = localStorage.getItem(cacheKey);
    if (!raw) return null;

    const cached: CachedBrand = JSON.parse(raw);
    if (Date.now() - cached.cachedAt > CACHE_TTL_MS) {
      localStorage.removeItem(cacheKey);
      return null;
    }
    return cached.brand;
  } catch {
    return null;
  }
}

export function setCachedBrand(brand: BrandConfig, key?: string): void {
  try {
    const cacheKey = key ? `${BRAND_CACHE_KEY}:${key}` : BRAND_CACHE_KEY;
    const cached: CachedBrand = { brand, cachedAt: Date.now() };
    localStorage.setItem(cacheKey, JSON.stringify(cached));
  } catch { /* ignore */ }
}

export function clearBrandCache(): void {
  try {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(BRAND_CACHE_KEY));
    keys.forEach(k => localStorage.removeItem(k));
    localStorage.removeItem(DOMAIN_MAP_CACHE_KEY);
  } catch { /* ignore */ }
}

// ─────────────────────────────────────────────────────────────────────────────
// Apply Brand to Document (Pre-React)
// ─────────────────────────────────────────────────────────────────────────────

export function applyBrandToDocument(brand: BrandConfig): void {
  // Title
  if (brand.name) {
    document.title = brand.name;
  }

  // Favicon
  if (brand.favicon) {
    let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = brand.favicon;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Brand Validation (Security)
// ─────────────────────────────────────────────────────────────────────────────

const ALLOWED_IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.svg', '.ico', '.webp'];
const MAX_URL_LENGTH = 2048;

export function isValidBrandAssetUrl(url: string): boolean {
  if (!url) return true; // empty is ok
  if (url.length > MAX_URL_LENGTH) return false;

  // Must be HTTPS or relative path or S3 URL
  if (!url.startsWith('https://') && !url.startsWith('/') && !url.startsWith('data:image/')) {
    return false;
  }

  // Check extension for non-data URLs
  if (!url.startsWith('data:')) {
    const urlWithoutQuery = url.split('?')[0].toLowerCase();
    if (!ALLOWED_IMAGE_EXTENSIONS.some(ext => urlWithoutQuery.endsWith(ext))) {
      // Allow S3 keys that don't have extensions
      if (!url.includes('s3') && !url.includes('amazonaws.com')) {
        return false;
      }
    }
  }

  return true;
}

export function sanitizeBrandConfig(raw: Partial<BrandConfig>): BrandConfig {
  return {
    programId: raw.programId || null,
    programCode: raw.programCode || null,
    name: (raw.name || DEFAULT_BRAND.name).slice(0, 100),
    logo: isValidBrandAssetUrl(raw.logo || '') ? (raw.logo || '') : '',
    favicon: isValidBrandAssetUrl(raw.favicon || '') ? (raw.favicon || '') : '',
    sidebarLogo: isValidBrandAssetUrl(raw.sidebarLogo || '') ? (raw.sidebarLogo || '') : '',
    theme: {
      primaryColor: raw.theme?.primaryColor || DEFAULT_BRAND.theme.primaryColor,
      secondaryColor: raw.theme?.secondaryColor || DEFAULT_BRAND.theme.secondaryColor,
      accentColor: raw.theme?.accentColor || DEFAULT_BRAND.theme.accentColor,
    },
    domain: raw.domain || null,
    loginBackground: raw.loginBackground || DEFAULT_BRAND.loginBackground,
    loginPageTitle: (raw.loginPageTitle || DEFAULT_BRAND.loginPageTitle).slice(0, 200),
    loginPageSubtitle: (raw.loginPageSubtitle || DEFAULT_BRAND.loginPageSubtitle).slice(0, 200),
    loginPageDescription: (raw.loginPageDescription || DEFAULT_BRAND.loginPageDescription).slice(0, 500),
    resolvedAt: new Date().toISOString(),
  };
}
